import React, { useRef, useState, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';

const slides = [
  {
    title: 'Chiptuning',
    description: 'Maximaliseer het potentieel van uw voertuig met professionele chiptuning. Onze geavanceerde software-optimalisatie zorgt voor meer vermogen, beter koppel en een soepelere rijervaring – volledig afgestemd op uw motor.',
    image: 'https://lozejxkhsvwkksgzhrfd.supabase.co/storage/v1/object/public/images/chiptuning%20(1).jpg',
  },
  {
    title: 'PPF & Lakbescherming',
    description: 'Bescherm de lak van uw waardevolle voertuig met premium Paint Protection Film. Onzichtbare maar effectieve bescherming tegen steenslag, krassen en UV-straling met zelfhelende eigenschappen.',
    image: 'https://lozejxkhsvwkksgzhrfd.supabase.co/storage/v1/object/public/images/ppfwraps.jpg',
  },
  {
    title: 'Performance Tuning',
    description: 'Premium uitlaatsystemen en performance upgrades voor de perfecte balans tussen prestaties en geluid. Van subtiel sportief tot volledig race-klaar – vakkundig gemonteerd door onze specialisten.',
    image: 'https://lozejxkhsvwkksgzhrfd.supabase.co/storage/v1/object/public/images/Tuning-4.jpg',
  },
  {
    title: 'Modificaties',
    description: 'Van performance upgrades tot esthetische aanpassingen op maat. Wij transformeren uw voertuig met hoogwaardige onderdelen en vakkundig maatwerk door onze gecertificeerde specialisten.',
    image: 'https://lozejxkhsvwkksgzhrfd.supabase.co/storage/v1/object/public/images/mods.jpg',
  },
  {
    title: 'Detailing',
    description: 'Breng uw voertuig terug naar absolute showroom conditie. Van paint correction tot ceramic coating – elk detail wordt met precisie behandeld voor een onberispelijk resultaat.',
    image: 'https://lozejxkhsvwkksgzhrfd.supabase.co/storage/v1/object/public/images/Vermado%20automotive_Website%20fotos%20%2010%20april-25.jpg',
  },
  {
    title: 'Tuning Kits',
    description: 'Complete tuning pakketten voor optimale prestaties en betrouwbaarheid. Professioneel samengestelde kits met hoogwaardige componenten, volledig plug & play voor uw voertuig.',
    image: 'https://lozejxkhsvwkksgzhrfd.supabase.co/storage/v1/object/public/images/tuningkits.jpg',
  },
];

const SLIDE_WIDTH_DESKTOP = 80;
const SLIDE_WIDTH_MOBILE = 90;
const GAP_VW = 5;

const WorkProcessSlider = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isSticky, setIsSticky] = useState(false);

  const handleScroll = useCallback(() => {
    if (!sectionRef.current) return;
    const rect = sectionRef.current.getBoundingClientRect();
    const sectionHeight = sectionRef.current.offsetHeight;
    const viewportHeight = window.innerHeight;
    const scrollableDistance = sectionHeight - viewportHeight;

    if (rect.top <= 0 && rect.bottom >= viewportHeight) {
      setIsSticky(true);
      const scrolled = -rect.top;
      const progress = Math.min(1, Math.max(0, scrolled / scrollableDistance));
      setScrollProgress(progress);
    } else {
      setIsSticky(false);
      if (rect.top > 0) setScrollProgress(0);
      if (rect.bottom < viewportHeight) setScrollProgress(1);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const SLIDE_WIDTH_VW = isMobile ? SLIDE_WIDTH_MOBILE : SLIDE_WIDTH_DESKTOP;
  const totalScrollWidth = slides.length * (SLIDE_WIDTH_VW + GAP_VW);
  const translateX = -scrollProgress * (totalScrollWidth - 100);

  const currentSlideIndex = Math.min(
    slides.length - 1,
    Math.floor(scrollProgress * slides.length)
  );

  return (
    <section
      ref={sectionRef}
      className="relative bg-black"
      style={{ height: `${slides.length * 100}vh` }}
    >
      <div
        className={`${isSticky ? 'fixed top-0 left-0 right-0' : 'relative'} h-screen w-full overflow-hidden flex flex-col`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-8 md:px-12 lg:px-16 pt-28 pb-8 flex-shrink-0">
          <h2
            className="text-white/50 text-sm tracking-wide"
            style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 400 }}
          >
            Onze Specialiteiten
          </h2>
          <div
            className="flex items-center gap-0.5 text-sm"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            <span className="text-[#B1A06D] font-semibold">
              {String(currentSlideIndex + 1).padStart(2, '0')}.
            </span>
            <span className="text-white/30 mx-1.5">/</span>
            <span className="text-white/30">
              {String(slides.length).padStart(2, '0')}.
            </span>
          </div>
        </div>

        {/* Horizontal sliding track */}
        <div className="flex-1 min-h-0 overflow-hidden">
          <div
            className="h-full flex items-stretch gap-[3vw] pl-8 md:pl-12 lg:pl-16"
            style={{
              width: `${totalScrollWidth}vw`,
              transform: `translateX(${translateX}vw)`,
              willChange: 'transform',
            }}
          >
            {slides.map((slide, i) => (
              <div
                key={i}
                className="flex-shrink-0 h-full flex flex-col md:flex-row gap-4 md:gap-8 lg:gap-12"
                style={{ width: `${SLIDE_WIDTH_VW}vw` }}
              >
                {/* Image */}
                <div className="relative rounded-xl overflow-hidden h-[55%] md:h-full md:w-[55%]">
                  <img
                    src={slide.image}
                    alt={slide.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Text */}
                <div className="flex flex-col justify-end pb-2 md:pb-[8%] md:w-[40%]">
                  <h3
                    className="text-white mb-3 md:mb-5 uppercase"
                    style={{
                      fontFamily: "'Manrope', sans-serif",
                      fontSize: 'clamp(1.2rem, 2.2vw, 1.85rem)',
                      fontWeight: 600,
                      lineHeight: 1.2,
                      letterSpacing: '0.08em',
                    }}
                  >
                    {slide.title}
                  </h3>
                  <p
                    className="text-white/60 leading-[1.75] max-w-[300px] text-[12px] md:text-[13px]"
                    style={{
                      fontFamily: "'Manrope', sans-serif",
                    }}
                  >
                    {slide.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Progress bar */}
        <div className="flex-shrink-0 px-8 md:px-12 lg:px-16 pb-8 pt-6">
          <div className="relative h-4 flex items-center">
            <div className="absolute left-0 top-0 bottom-0 w-2">
              <div className="absolute top-0 left-0 w-full h-[1px] bg-white/20" />
              <div className="absolute top-0 left-0 w-[1px] h-full bg-white/20" />
              <div className="absolute bottom-0 left-0 w-full h-[1px] bg-white/20" />
            </div>
            <div className="absolute right-0 top-0 bottom-0 w-2">
              <div className="absolute top-0 right-0 w-full h-[1px] bg-white/20" />
              <div className="absolute top-0 right-0 w-[1px] h-full bg-white/20" />
              <div className="absolute bottom-0 right-0 w-full h-[1px] bg-white/20" />
            </div>
            <div className="mx-4 flex-1 relative h-[1px]">
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage: 'repeating-linear-gradient(to right, rgba(255,255,255,0.12) 0, rgba(255,255,255,0.12) 8px, transparent 8px, transparent 16px)',
                }}
              />
              <motion.div
                className="absolute top-0 left-0 h-full bg-[#B1A06D]"
                style={{ width: `${scrollProgress * 100}%` }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WorkProcessSlider;
